import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { DisclaimerBanner, GeneralDialog, LoadingIndicator, LoadingStatusWrapper } from 'sharedComponents';

import { apiCallStatus } from 'helpers/constants';
import { loggerCommands } from './constants';
import { getSharedLoggerDisplayName } from './helpers';
import { TriggerDataLogger } from './TriggerDataLogger';
import { BlackboxLogger } from './BlackboxLogger';
import { SpecialLogger } from './SpecialLogger';
import { SubscribeAndUnsubscribeLogger } from './SubscribeAndUnsubscribe';
import './style.scss';


const DataLoggerComponent = ({ model, actions, asset, show, close }) => {
    const { t: translate } = useTranslation();
    const { triggerLoggerCommands = [],
        specialLoggerCommands = [],
        SubscibeUnsubscribeLoggerCommands = [],
        dataLoggerCommandsLodingStatus,
        subscribeWarningTextKey,
        amcosWarningTextKey,
        SubscribeUnsubscribeWarningTextKey,
        triggerDataLoggerCommandsLoadingStatus,
        subscribeAndUnsubscribeCommandsLoadingStatus,
        specialLoggerCommandsLoadingStatus
    } = model || {};

    const listofDataLoggerCommands = [...triggerLoggerCommands, ...specialLoggerCommands, ...SubscibeUnsubscribeLoggerCommands];
    const assetId = asset?.id;

    useEffect(() => {
        if (assetId) {
            actions?.getDataLoggerCommands(assetId);
        }
    }, [assetId]);

    const renderLoadingStatusWrapper = (children) => <LoadingStatusWrapper
        indicatorConfig={{ size: 'small', style: { 'padding': '8px 0px' } }}
        transparent
        loadingStatus={[triggerDataLoggerCommandsLoadingStatus, specialLoggerCommandsLoadingStatus, subscribeAndUnsubscribeCommandsLoadingStatus]}
    >
        {children}
    </LoadingStatusWrapper>;

    const renderDataLoggerCommands = () => {
        if (dataLoggerCommandsLodingStatus === apiCallStatus.LOADING) {
            return <LoadingIndicator />;
        }

        if (dataLoggerCommandsLodingStatus === apiCallStatus.SUCCESS) {
            const subscribeUnsubscribeCommands = listofDataLoggerCommands?.filter(cmd =>
                cmd.commandType === loggerCommands.SUBSCRIBEUNSUBSCRIBE &&
                (cmd.loggerType !== loggerCommands.USERLOGGERDATA &&
                    cmd.loggerType !== loggerCommands.DATALOGGER1 &&
                    cmd.loggerType !== loggerCommands.DATALOGGER2 &&
                    cmd.loggerType !== loggerCommands.DATALOGGER3)
            );
            const hasSubscribeUnsubscribeCommands = subscribeUnsubscribeCommands?.length > 0;
            const hasTriggerCommands = listofDataLoggerCommands?.some(cmd =>
                cmd.commandType === loggerCommands.TRIGGER ||
                cmd.commandType === loggerCommands.STARTSTOP
            );
            const subscribeUnsubscribeLoggerCommands = listofDataLoggerCommands?.filter(cmd =>
                cmd.commandType === loggerCommands.SUBSCRIBEUNANDSUBSCRIBE &&
                (cmd.loggerType === loggerCommands.USERLOGGERDATA ||
                    cmd.loggerType === loggerCommands.DATALOGGER1 ||
                    cmd.loggerType === loggerCommands.DATALOGGER2 ||
                    cmd.loggerType === loggerCommands.DATALOGGER3)
            );
            const hasSubscribeUnsubscribeLoggerCommands = subscribeUnsubscribeLoggerCommands?.length > 0;

            // Only include the logger type in the heading when a section represents a single logger type
            const subscribeLoggerDisplayName = getSharedLoggerDisplayName(subscribeUnsubscribeCommands);
            const getSubscribeSectionTitle = (loggerDisplayName) => loggerDisplayName
                ? translate('ABB.Powertrain.Frontend.dataLoggerSubscribeTitle', { loggerType: loggerDisplayName })
                : translate('ABB.Powertrain.Frontend.dataLoggerSubscribeGenericTitle');

            return (
                <Container className='data-logger-modal-container g-0'>
                    {
                        hasSubscribeUnsubscribeCommands && <>
                            <div className='data-logger__sub-heading mb-2'>{getSubscribeSectionTitle(subscribeLoggerDisplayName)}</div>
                            {subscribeWarningTextKey && <DisclaimerBanner className='data-logger__disclaimer_text' text={translate(`ABB.Powertrain.Frontend.${subscribeWarningTextKey}`)} />}
                            <div className='data-logger__list-container'>
                                {renderLoadingStatusWrapper(<SpecialLogger asset={asset} />)}
                            </div>
                        </>
                    }
                    {
                        hasSubscribeUnsubscribeLoggerCommands && <>
                            <div className='data-logger__sub-heading mb-2'>{translate('ABB.Powertrain.Frontend.dataLoggerSubscribeGenericTitle')}</div>
                            {SubscribeUnsubscribeWarningTextKey && <DisclaimerBanner className='data-logger__disclaimer_text' text={translate(`ABB.Powertrain.Frontend.${SubscribeUnsubscribeWarningTextKey}`)} />}
                            <div className='data-logger__list-container'>
                                {renderLoadingStatusWrapper(<SubscribeAndUnsubscribeLogger asset={asset} />)}
                            </div>
                        </>
                    }
                    {hasSubscribeUnsubscribeCommands && hasTriggerCommands && <hr className='data-logger__type-separator' />}
                    {
                        hasTriggerCommands && <>
                            <div className='data-logger__sub-heading'>{`${translate('ABB.Powertrain.Frontend.dataLoggerTriggerTitle')}`}</div>
                            {amcosWarningTextKey && <DisclaimerBanner className='data-logger__disclaimer_text' text={translate(`ABB.Powertrain.Frontend.${amcosWarningTextKey}`)} />}
                            <div className='data-logger__list-container'>
                                {renderLoadingStatusWrapper(
                                    <>
                                        <TriggerDataLogger asset={asset} />
                                        <BlackboxLogger asset={asset} />
                                    </>
                                )}
                            </div>
                        </>
                    }
                </Container>
            );
        }
        return null;
    };

    return <GeneralDialog
        modalClassName='data-logger-modal'
        show={show}
        close={close}
        title={`${translate('ABB.Powertrain.Frontend.dataLoggerTitle')}`}
        closeButton={true}
        acceptButtonProps={{
            text: translate('ABB.Powertrain.Frontend.closeBtnLbl'),
            onClick: () => { close(); }
        }}
    >
        {renderDataLoggerCommands()}
    </GeneralDialog>;
};

DataLoggerComponent.propTypes = {
    model: PropTypes.object,
    asset: PropTypes.object,
    show: PropTypes.bool,
    close: PropTypes.func,
    actions: PropTypes.shape({
        getDataLoggerCommands: PropTypes.func,
    })
};

export default DataLoggerComponent;
